import { useContext, useState, useEffect } from "react";
import { PrintContext } from "../context/PrintProvider/PrintProvider";
import usePopup from "./usePopup";

type FileType = "pdf" | "image" | "unknown";
type ViewType = "iframe" | "new-tab";

const useViewFiles = (): {
  viewFile: (base64File: string, viewType?: ViewType) => void;
  loading: boolean;
  currentFileUrl: string | null;
  detectedFileType: FileType;
} => {
  const { iframeRef } = useContext(PrintContext);
  const { showSnackbar } = usePopup();
  const [loading, setLoading] = useState<boolean>(false);
  const [currentFileUrl, setCurrentFileUrl] = useState<string | null>(null);
  const [detectedFileType, setDetectedFileType] = useState<FileType>("unknown");

  // Function to detect file type from base64 string
  const detectFileType = (base64: string): FileType => {
    try {
      const base64Prefix = base64.split(",")[0]; // Handle data URI scheme
      if (base64Prefix.includes("application/pdf")) return "pdf";
      if (base64Prefix.includes("image/")) return "image";

      // Decode first few bytes
      const decoded = atob(base64.split(",")[1] || base64);
      const firstFewBytes = decoded.slice(0, 4);

      // PDF signature: %PDF
      if (firstFewBytes === "%PDF") return "pdf";

      // Common image signatures
      const imageSignatures = ["\xFF\xD8\xFF", "\x89\x50\x4E\x47", "\x47\x49\x46\x38", "\x42\x4D"];
      if (imageSignatures.some((sig) => decoded?.startsWith(sig))) return "image";

      return "unknown";
    } catch (error) {
      console.error("Error detecting file type:", error);
      return "unknown";
    }
  };

  const viewFile = (base64String: string, viewType: ViewType = "new-tab") => {
    try {
      setLoading(true);

      // Detect file type
      const fileType = detectFileType(base64String);
      setDetectedFileType(fileType);

      // Convert Base64 to Blob
      const byteString = atob(base64String.split(",")[1] || base64String);
      const arrayBuffer = new Uint8Array(byteString.length);
      for (let i = 0; i < byteString.length; i++) {
        arrayBuffer[i] = byteString.charCodeAt(i);
      }

      const mimeType = fileType === "pdf" ? "application/pdf" : "image/png";
      const blob = new Blob([arrayBuffer], { type: mimeType });
      const url = URL.createObjectURL(blob);

      setCurrentFileUrl(url); // Store the URL to clean it up later

      if (viewType === "iframe" && iframeRef?.current) {
        // Load inside an iframe
        iframeRef.current.src = url;
      } else {
        // Open in a new tab
        window.open(url, "_blank");
      }
    } catch (error) {
      console.error("Error displaying file:", error);
      showSnackbar({
        message:"Error displaying file",
        open:true,
        variant:"error",
        duration:2000
      });
    } finally {
      setLoading(false);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (currentFileUrl) {
        URL.revokeObjectURL(currentFileUrl);
      }
    };
  }, [currentFileUrl]);

  return {
    viewFile,
    loading,
    currentFileUrl,
    detectedFileType,
  };
};

export default useViewFiles;
